import React from 'react';
import moment from 'moment';
import {isEqual} from 'lodash';
import {Button, Icon, Text, WithTooltip} from '@albert-io/atomic';

import {AnalyticsApiResponse, Dimension} from 'client/Reports/reports.types';
import {Link} from 'react-router';

export const getStudentAssignmentsView = (data: AnalyticsApiResponse['dimensions']) => {
  if (data.length === 1) {
    return data[0].value.student_assignments_view?.[0];
  }
  const student = data.find((d) => d.type === 'students')?.value;
  const assignment = data.find((d) => d.type === 'assignments')?.value;

  return student?.student_assignments_view?.find(
    (studentAssignment) => studentAssignment.assignment_id === assignment?.id
  );
};

const getAssignment = (data: AnalyticsApiResponse['dimensions']) => {
  const assignmentDimension = data.find((d) => d.type === 'assignments')?.value;
  if (assignmentDimension) {
    return assignmentDimension;
  }
  return data[0].value.student_assignments_view?.[0].assignment;
};

interface Props {
  data: AnalyticsApiResponse['dimensions'];
  dimensions: Dimension[];
}

const sortClassroomAssignments = (classroomAssignments: any[]) =>
  classroomAssignments.sort(
    (a, b) => new Date(b.settings?.due_date).getTime() - new Date(a.settings?.due_date).getTime()
  );

const getClassroomId = (data: AnalyticsApiResponse['dimensions'], dimensions: Dimension[]) => {
  if (isEqual(dimensions, ['assignments'])) {
    const assignment = data[0].value;
    const classroomAssignments = sortClassroomAssignments(assignment.classroom_assignments);

    return classroomAssignments[0].classroom_id;
  }

  if (isEqual(dimensions, ['students'])) {
    const {assignment} = getStudentAssignmentsView(data);
    const classroomAssignments = sortClassroomAssignments(assignment.classroom_assignments);

    return classroomAssignments[0].classroom_id;
  }

  if (isEqual(dimensions, ['students', 'assignments'])) {
    const assignment = data.find((d) => d.type === 'assignments')?.value;
    const classroomAssignments = sortClassroomAssignments(assignment.classroom_assignments);
    return classroomAssignments[0].classroom_id;
  }

  return undefined;
};

const TooltipContent = ({data, dimensions}: Props) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const {submitted, assignment_id, student_id} = getStudentAssignmentsView(data);

  const classroomId = getClassroomId(data, dimensions);

  if (!classroomId) {
    return null;
  }

  return (
    <div
      className='u-display_flex u-align-items_center u-flex-direction_column u-pad_1'
      style={{minWidth: 250}}
    >
      <Text size='s'>
        {submitted
          ? `Submitted ${moment(submitted).format('L')} @ ${moment(submitted).format('LT')}`
          : 'Not Submitted'}
      </Text>
      <Button
        to={`/classes/${classroomId}/assignments/${assignment_id}/student-detail/${student_id}/view`}
        variant='outlined'
        className='u-mar-t_1 u-width_100pc'
        as={Link}
        target='_blank'
        onClick={(e) => e.stopPropagation()}
      >
        View Submission
      </Button>
    </div>
  );
};

const AssignmentSubmitted = ({data, dimensions}: Props) => {
  const {submitted} = getStudentAssignmentsView(data) || {};
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const {due_date} = getAssignment(data) || {};

  if (submitted > due_date) {
    return (
      <WithTooltip
        content={<TooltipContent data={data} dimensions={dimensions} />}
        theme='light'
        placement='right'
      >
        <div className='u-display_flex u-align-items_center reports_submission-badge u-bgc_yellow-050 u-color_yellow-700 u-border-radius_pill'>
          <span className='u-mar-r_1'>Late</span>
          <Icon icon='info-circle' iconStyle='regular' />
        </div>
      </WithTooltip>
    );
  }

  if (submitted) {
    return (
      <WithTooltip
        content={<TooltipContent data={data} dimensions={dimensions} />}
        theme='light'
        placement='right'
      >
        <div className='u-display_flex u-align-items_center reports_submission-badge u-bgc_green-050 u-color_green-800 u-border-radius_pill'>
          <span className='u-mar-r_1'>Submitted</span>
          <Icon icon='info-circle' iconStyle='regular' />
        </div>
      </WithTooltip>
    );
  }

  return (
    <WithTooltip
      content={<TooltipContent data={data} dimensions={dimensions} />}
      theme='light'
      placement='right'
    >
      <div className='reports_submission-badge u-bgc_white u-color_slate-500 u-border-radius_pill'>
        <span className='u-mar-r_1'>Incomplete</span>
      </div>
    </WithTooltip>
  );
};

export default AssignmentSubmitted;
